import { BackgroundTaskType } from "@api/progress-api/progress-api-types";
import { SdbBackgroundTask } from "@custom-types/sdb-background-tasks-types";
import { createSelector } from "@reduxjs/toolkit";
import { sdbBackgroundTasksAdapter } from "@store/sdb-background-tasks/sdb-background-tasks-slice";
import { RootState } from "@store/store-helper";
import { FetchingStatus, StringEntityId } from "@store/store-types";
import {
  getTasksContext,
  sortSdbBackgroundTasks,
} from "@utils/sdb-background-tasks-utils";

/**
 * @returns all sdbBackgroundTasks entities
 */
export const sdbBackgroundTasksSelector: (
  state: RootState
) => SdbBackgroundTask[] = createSelector(
  // eslint-disable-next-line no-restricted-syntax -- getTasksContext needs full state to extract element context
  (state: RootState) => state,
  (state: RootState) => {
    const tasks = sdbBackgroundTasksAdapter
      .getSelectors()
      .selectAll(state.sdbBackgroundTasks);

    const tasksWithContext = getTasksContext({
      tasks,
      state,
    });

    return sortSdbBackgroundTasks(tasksWithContext);
  }
);

/**
 * @returns the before page token for the background tasks or null if not known yet
 */
export const sdbBackgroundTaskBeforePageSelector: (
  state: RootState
) => StringEntityId | null = createSelector(
  (state: RootState) => state.sdbBackgroundTasks,
  (sdbBackgroundTasks) => sdbBackgroundTasks.pagination.before
);

/**
 * @returns the total count of the background tasks or null if not known yet
 */
export const totalCountSelector: (state: RootState) => number | null =
  createSelector(
    (state: RootState) => state.sdbBackgroundTasks,
    (sdbBackgroundTasks) => sdbBackgroundTasks.pagination.totalCount
  );

/**
 * Returns a selector that gets the total count for a specific background task type.
 * @param taskType The type of background task to get the count for
 * @returns A selector that returns the total count for that task type, or null if not known
 */
export function totalCountByTypeSelector(
  taskType: BackgroundTaskType
): (state: RootState) => number | null {
  return createSelector(
    (state: RootState) => state.sdbBackgroundTasks,
    (sdbBackgroundTasks) =>
      sdbBackgroundTasks.pagination.totalCountByType[taskType] ?? null
  );
}

/**
 * @returns the fetching status of the background tasks
 */
export const backgroundTasksFetchingStatusSelector: (
  state: RootState
) => FetchingStatus = createSelector(
  (state: RootState) => state.sdbBackgroundTasks,
  (sdbBackgroundTasks) =>
    sdbBackgroundTasks.fetching.backgroundTasksFetchingStatus
);

/**
 * Returns a selector that gets the fetching status for a specific background task type.
 * This prevents race conditions when multiple hooks fetch different task types simultaneously.
 * @param taskType The type of background task to get the fetching status for
 * @returns A selector that returns the fetching status for that task type, or "uninitialized" if not known
 */
export function fetchingStatusByTypeSelector(
  taskType: BackgroundTaskType
): (state: RootState) => FetchingStatus {
  return createSelector(
    (state: RootState) => state.sdbBackgroundTasks,
    (sdbBackgroundTasks) =>
      sdbBackgroundTasks.fetching.fetchingStatusByType[taskType] ??
      "uninitialized"
  );
}
